import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import {formatCalendarDateIsoSegment, parseCalendarDateFromIsoString} from "~/utilities/calendarDateIso";
import {DateTime} from "luxon";

type PickerDateValue = Date | string | null;

/** Mantine v9 range picker emits `YYYY-MM-DD` strings; normalize before compare/apply. */
export function toPickerDate(value: PickerDateValue): Date | null {
    if (value === null) {
        return null;
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime()) === true) {
            return null;
        }
        return value;
    }

    const fromCalendar = parseCalendarDateFromIsoString(value);
    if (fromCalendar !== null) {
        return new Date(fromCalendar.year, fromCalendar.month - 1, fromCalendar.day);
    }

    const fromFormat = DateTime.fromFormat(value, "yyyy-MM-dd");
    if (fromFormat.isValid === true) {
        return new Date(fromFormat.year, fromFormat.month - 1, fromFormat.day);
    }

    return null;
}

export const DATE_RANGE_PILL_EMPTY_LABEL = "Date Range";

export function formatDateRangePillLabel(start: Iso8601DateTime | null, end: Iso8601DateTime | null, emptyLabel: string = DATE_RANGE_PILL_EMPTY_LABEL): string {
    if (start === null && end === null) {
        return emptyLabel;
    }
    const fmt = (iso: string, includeYear: boolean) => formatCalendarDateIsoSegment(iso, includeYear);
    if (start !== null && end !== null) {
        const startDt = parseCalendarDateFromIsoString(start);
        const endDt = parseCalendarDateFromIsoString(end);
        if (startDt === null || endDt === null) {
            return DATE_RANGE_PILL_EMPTY_LABEL;
        }
        if (startDt.hasSame(endDt, "day")) {
            return fmt(start, true);
        }
        const sameYear = startDt.year === endDt.year;
        return `${fmt(start, !sameYear)} - ${fmt(end, true)}`;
    }
    return fmt(start !== null ? start : end !== null ? end : "", true);
}
